// @ts-nocheck
import React, { useState } from 'react';
import WidgetWrapper from './WidgetWrapper';
import DashboardCustomizer from './DashboardCustomizer';
import KPIWidgets from './widgets/KPIWidgets';
import AttentionWidget from './widgets/AttentionWidget';
import PerformanceWidget from './widgets/PerformanceWidget';
import TopPerformersWidget from './widgets/TopPerformersWidget';
import QuickActionsWidget from './widgets/QuickActionsWidget';
import TeamActivityWidget from './widgets/TeamActivityWidget';
import dashboardCustomizationService from '../../services/dashboardCustomizationService';
import { Layout, Eye, EyeOff, Settings } from 'lucide-react';
import Dashboard from './Dashboard';
import { isDevMode } from 'lib/featureFlags';
import { PageContainer } from '../ui/page-container';
import { EmptyState } from 'components/ui/empty-state';

const DashboardGrid = ({ config, onConfigChange }) => {
    const [editMode, setEditMode] = useState(false);
    const [draggedWidget, setDraggedWidget] = useState(null);
    const [showCustomizer, setShowCustomizer] = useState(false);
    const [dragOverWidget, setDragOverWidget] = useState(null);

    // Widget component mapping
    const widgetComponents = {
        kpi: KPIWidgets,
        attention: AttentionWidget,
        performance: PerformanceWidget,
        topPerformers: TopPerformersWidget,
        quickActions: QuickActionsWidget,
        teamActivity: TeamActivityWidget
    };

    // Get enabled widgets sorted by order
    const enabledWidgets = config?.layout?.widgets
        ?.filter(widget => widget.enabled)
        ?.sort((a, b) => a.order - b.order) || [];

    // Handle widget visibility toggle
    const toggleWidgetVisibility = async (widgetId) => {
        if (!config || !onConfigChange) return;

        const updatedConfig = await dashboardCustomizationService.toggleWidgetVisibility(config, widgetId);
        onConfigChange(updatedConfig);

        // Track behavior
        await dashboardCustomizationService.trackBehavior(config.userId, 'widget_visibility_toggle', {
            widgetId,
            enabled: updatedConfig.layout.widgets.find(w => w.id === widgetId)?.enabled
        });
    };

    // Handle drag start
    const handleDragStart = (e, widget) => {
        setDraggedWidget(widget);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.outerHTML);
    };

    // Handle drag over
    const handleDragOver = (e, widget) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverWidget(widget);
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setDragOverWidget(null);
    };

    // Handle drop
    const handleDrop = async (e, targetWidget) => {
        e.preventDefault();
        setDragOverWidget(null);

        if (!draggedWidget || !targetWidget || draggedWidget.id === targetWidget.id) {
            setDraggedWidget(null);
            return;
        }

        // Reorder widgets
        const widgets = [...config.layout.widgets];
        const draggedIndex = widgets.findIndex(w => w.id === draggedWidget.id);
        const targetIndex = widgets.findIndex(w => w.id === targetWidget.id);

        // Remove dragged widget and insert at target position
        const [removed] = widgets.splice(draggedIndex, 1);
        widgets.splice(targetIndex, 0, removed);

        // Update orders
        const updatedWidgets = widgets.map((widget, index) => ({
            ...widget,
            order: index + 1
        }));

        const updatedConfig = {
            ...config,
            layout: {
                ...config.layout,
                widgets: updatedWidgets
            }
        };

        onConfigChange(updatedConfig);
        await dashboardCustomizationService.saveConfiguration(updatedConfig);

        // Track behavior
        await dashboardCustomizationService.trackBehavior(config.userId, 'widget_reorder', {
            widgetId: draggedWidget.id,
            fromOrder: draggedWidget.order,
            toOrder: targetWidget.order
        });

        setDraggedWidget(null);
    };

    // Get grid class based on widget size
    const getGridClass = (size) => {
        switch (size) {
            case 'full':
                return 'col-span-12';
            case 'half':
                return 'col-span-12 lg:col-span-6';
            case 'third':
                return 'col-span-12 md:col-span-6 lg:col-span-4';
            case 'quarter':
                return 'col-span-12 sm:col-span-6 lg:col-span-3';
            default:
                return 'col-span-12 lg:col-span-6';
        }
    };

    // Handle drill-down navigation
    const handleDrillDown = (drillDownData) => {
        console.log('Drill-down requested:', drillDownData);
        // In a real app, this would navigate to detailed analytics
        // For now, we'll just log the drill-down data
    };

    // Render widget component
    const renderWidget = (widget) => {
        const WidgetComponent = widgetComponents[widget.type];

        if (!WidgetComponent) {
            return (
                <div className="p-6 text-center text-content-muted">
                    <p>Widget type "{widget.type}" not found</p>
                </div>
            );
        }

        // Pass additional props for specific widgets
        const widgetProps = {
            config,
            ...(widget.type === 'performance' && { onDrillDown: handleDrillDown })
        };

        return <WidgetComponent {...widgetProps} />;
    };

    return (
        <PageContainer>
            {/* Dashboard Controls */}
            {
                isDevMode() &&
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <p className="text-ui text-content-muted">
                            {editMode ? 'Drag widgets to reorder them' : 'Your personalised marketing overview'}
                        </p>
                    </div>

                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => setEditMode(!editMode)}
                            aria-pressed={editMode}
                            className={editMode ? 'btn-primary text-ui px-4 py-2' : 'btn-secondary text-ui px-4 py-2'}
                        >
                            <Layout className="w-4 h-4" />
                            <span>{editMode ? 'Done Editing' : 'Edit Layout'}</span>
                        </button>

                        <button
                            onClick={() => setShowCustomizer(true)}
                            className="btn-secondary text-ui px-4 py-2"
                        >
                            <Settings className="w-4 h-4" />
                            <span>Customize</span>
                        </button>

                        {/* My Dashboard Component */}


                        {editMode && (
                            <div className="flex items-center gap-2 bg-surface-sunken border border-line rounded-xl p-2">
                                <span className="text-meta text-content-muted">Show/Hide:</span>
                                {config?.layout?.widgets?.map(widget => (
                                    <button
                                        key={widget.id}
                                        onClick={() => toggleWidgetVisibility(widget.id)}
                                        aria-pressed={widget.enabled}
                                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-micro font-semibold transition-colors focus-ring border ${widget.enabled
                                            ? 'bg-success-subtle text-success-on-subtle border-success-border'
                                            : 'bg-surface-sunken text-content-muted border-line'
                                            }`}
                                    >
                                        {widget.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                        <span className="capitalize">{widget.type}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            }

            <Dashboard />

            {/* Dashboard Grid */}
            {isDevMode() &&
                <div className="grid grid-cols-12 gap-6">
                    {enabledWidgets.map((widget) => (
                        <div
                            key={widget.id}
                            className={`${getGridClass(widget.size)} ${editMode ? 'cursor-move' : ''
                                }`}
                            draggable={editMode}
                            onDragStart={(e) => handleDragStart(e, widget)}
                            onDragOver={(e) => handleDragOver(e, widget)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, widget)}
                        >
                            <WidgetWrapper
                                title={getWidgetTitle(widget.type)}
                                editMode={editMode}
                                onToggleVisibility={() => toggleWidgetVisibility(widget.id)}
                                isVisible={widget.enabled}
                            >
                                {renderWidget(widget)}
                            </WidgetWrapper>
                        </div>
                    ))}
                </div>
            }

            {/* Empty State */}
            {isDevMode() && enabledWidgets.length === 0 && (
                <EmptyState
                    icon={Layout}
                    title="No widgets enabled"
                    description="Turn widgets on to build the overview you want to see first each morning."
                    primaryAction={{ label: "Edit layout", onClick: () => setEditMode(true) }}
                />
            )}

            {/* Dashboard Customizer Modal */}
            {showCustomizer && (
                <DashboardCustomizer
                    config={config}
                    onConfigChange={onConfigChange}
                    editMode={editMode}
                    onEditModeChange={setEditMode}
                    onClose={() => setShowCustomizer(false)}
                />
            )}
        </PageContainer>
    );
};

// Helper function to get widget titles
const getWidgetTitle = (type) => {
    const titles = {
        kpi: 'Key Performance Indicators',
        attention: 'Needs Attention',
        performance: 'Performance Trends',
        topPerformers: 'Top Performers',
        quickActions: 'Quick Actions',
        teamActivity: 'Team Activity'
    };
    return titles[type] || type;
};

export default DashboardGrid;